// widget

// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@eisinc/App/Components/Notify";
import ScheduleOrderGroupHeader from "@eisinc/App/Components/ScheduleOrder/ScheduleOrderGroupHeader";
import ScheduleOrderLineItem, {
    LineScheduleState,
    ScheduleShipment,
} from "@eisinc/App/Components/ScheduleOrder/ScheduleOrderLineItem";
import { useScheduledLines } from "@eisinc/App/Contexts/ScheduledLinesContext";
import saveScheduledLines from "@eisinc/App/Handlers/saveScheduledLines";
import { CartGroup } from "@eisinc/App/Services/ScheduleOrderService";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { xIsAuthenticated } from "@eisinc/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@eisinc/App/Store/Selectors/SettingsSelectors";
import { SHIPS_ASAP, XNGAGE_ELEMENTS } from "@eisinc/XDL/constants";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, parseBoolean, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import DatePicker, { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import isEqual from "lodash/isEqual";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export type { LineScheduleState, ScheduleShipment };

export type LineScheduleMap = Record<string, LineScheduleState>;

export interface ScheduleOrderGroups {
    availableToSchedule: CartGroup[];
    schedulingUnavailable: CartGroup[];
}
export interface ScheduleOrderWidgetStyles {
    loadingOverlay?: LoadingOverlayProps;
    wrapper?: InjectableCss;
    bulkEditSection?: InjectableCss;
    bulkEditWrapper?: InjectableCss;
    bulkEditCheckboxWrapper?: InjectableCss;
    bulkEditDatePickerWrapper?: InjectableCss;
    bulkEditButtonWrapper?: InjectableCss;
    selectAllCheckbox?: CheckboxPresentationProps;
    bulkDatePicker?: DatePickerPresentationProps;
    addToSelectedButton?: ButtonPresentationProps;
    sectionWrapper?: InjectableCss;
    lineItemsWrapper?: InjectableCss;
    actionButtonsWrapper?: InjectableCss;
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    errorModal?: ModalPresentationProps;
    bulkEditSectionActionsWrapper?: InjectableCss;
    subHeading?: TypographyPresentationProps;
}

export const scheduleOrderWidgetStyles: ScheduleOrderWidgetStyles = {
    loadingOverlay: {
        css: css`
            min-height: 300px;
        `,
    },
    subHeading: {
        ...fontConfig.singleLineRegular,
        css: css`
            margin-bottom: 16px;
            display: block;
        `,
    },
    wrapper: {
        css: css`
            padding: 20px 0;
        `,
    },
    bulkEditSection: {
        css: css`
            margin-bottom: 12px;
            padding-top: 12px;
            border-top: 1px solid ${color("common.ui3")};
        `,
    },
    bulkEditWrapper: {
        css: css`
            display: flex;
            gap: 12px 8px;
            ${xScreen("lte", "sm")} {
                flex-wrap: wrap;
            }
        `,
    },
    bulkEditSectionActionsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;

            ${xScreen("lte", "sm")} {
                flex-direction: column;
                row-gap: 20px;
            }
        `,
    },
    bulkDatePicker: {
        cssOverrides: {
            datePicker: css`
                .react-datetime-picker {
                    height: 26px;
                    min-height: 26px;
                }
            `,
        },
        calendarIconProps: {
            size: 20,
            css: css`
                &&& {
                    padding: 3px;
                }
            `,
        },
    },
    bulkEditCheckboxWrapper: {
        css: css`
            align-items: center;
            display: flex;
            margin-right: 16px;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    selectAllCheckbox: {
        css: css`
            label {
                white-space: nowrap;
            }
        `,
    },
    bulkEditDatePickerWrapper: {},
    bulkEditButtonWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
        `,
    },
    addToSelectedButton: {
        sizeVariant: "small",
        css: css`
            width: 100%;
        `,
    },
    sectionWrapper: {
        css: css`
            &.unavailable {
                margin-top: 32px;
            }
        `,
    },
    lineItemsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    actionButtonsWrapper: {
        css: css`
            display: flex;
            gap: 24px;

            ${({ headerActions }: any) =>
                !headerActions &&
                css`
                    justify-content: flex-end;
                    padding: 12px 0;
                    border-top: 1px solid ${color("common.ui3")};

                    /* Sticky footer for better UX */
                    position: sticky;
                    bottom: 0;
                    background: white;
                    z-index: 10;
                `}
        `,
    },
    saveButton: {
        variant: "primary",
        sizeVariant: "small",
        css: css`
            ${xScreen("lte", "sm")} {
                &.saveButton {
                    margin-left: auto;
                }
            }
        `,
    },
    cancelButton: {
        sizeVariant: "small",
        variant: "secondary",
        css: css`
            ${xScreen("lte", "md")} {
                &.cancelButton {
                    display: none;
                }
            }
        `,
    },
};

const styles = scheduleOrderWidgetStyles;

const mapStateToProps = (state: CustomAppState) => {
    const cartState = getCurrentCartState(state);
    return {
        cart: cartState.value,
        isPickup: state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp,
        checkoutShippingPageUrl:
            getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url || "/checkoutReviewAndSubmit",
        isLoadingScheduledLines: state.xCommon.isLoadingScheduledLines,
        scheduledOrderMinimumTimeFrame: (getSettingsCollection(state) as any).customSettings
            ?.scheduledOrderMinimumTimeframe,
        isAuthenticated: xIsAuthenticated(state),
        allowBackOrderedProductsInScheduledOrders: parseBoolean(
            getSettingsCollection(state).customSettings.allowBackOrderedProductsInScheduledOrders,
        ),
    };
};

const mapDispatchToProps = {
    saveScheduledLines,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const generateShipmentId = (): string => {
    return `shipment-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Initialize schedule state for all cart lines
 * Each line starts with full qty assigned to "Ships ASAP" (if its the first time to visit the page)
 */
const initializeLineSchedules = (
    availableToSchedule: CartGroup[],
    schedulingUnavailable: CartGroup[],
): LineScheduleMap => {
    const schedules: LineScheduleMap = {};

    availableToSchedule.forEach(line => {
        // should append ships asap row to the beginning of the array if it doesn't exist
        const updatedLine = { ...line };
        if (updatedLine.items.length && updatedLine.items[0].requestedDeliveryDateDisplay !== SHIPS_ASAP) {
            updatedLine.items = [
                {
                    requestedDeliveryDate: null,
                    requestedDeliveryDateDisplay: SHIPS_ASAP,
                    qty: 0,
                },
                ...updatedLine.items,
            ];
        }
        schedules[line.cartLine.id] = {
            cartLineId: line.cartLine.id,
            productId: line.cartLine.productId || "",
            totalQty: line.cartLine.qtyOrdered || 0,
            isSelected: false,
            unitOfMeasure: line.cartLine.unitOfMeasure,
            items: !updatedLine.items?.length
                ? [
                      {
                          id: generateShipmentId(),
                          requestedDeliveryDate: null, // null = Ships ASAP
                          qty: updatedLine.cartLine.qtyOrdered || 0,
                      },
                  ]
                : updatedLine.items.map(item => {
                      return {
                          ...item,
                          id: generateShipmentId(),
                      };
                  }),
        };
    });

    // Unavailable lines - always Ships ASAP, no scheduling
    schedulingUnavailable.forEach(line => {
        schedules[line.cartLine.id] = {
            cartLineId: line.cartLine.id,
            productId: line.cartLine.productId || "",
            totalQty: line.cartLine.qtyOrdered || 0,
            isSelected: false,
            unitOfMeasure: line.cartLine.unitOfMeasure,
            items: [
                {
                    id: generateShipmentId(),
                    requestedDeliveryDate: null, // null = Ships ASAP
                    qty: line.cartLine.qtyOrdered || 0,
                },
            ],
        };
    });

    return schedules;
};

const ScheduleOrderWidget: React.FC<Props> = ({
    cart,
    scheduledOrderMinimumTimeFrame,
    isPickup,
    checkoutShippingPageUrl,
    isLoadingScheduledLines,
    saveScheduledLines,
    isAuthenticated,
    allowBackOrderedProductsInScheduledOrders,
}) => {
    const { groups: cartlinesGroups, errorMessage } = useScheduledLines();

    const [scheduleGroups, setScheduleGroups] = React.useState<ScheduleOrderGroups>({
        availableToSchedule: [],
        schedulingUnavailable: [],
    });

    const toasterContext = useContext(ToasterContext);

    const [lineSchedules, setLineSchedules] = React.useState<LineScheduleMap>({});

    // save initial state
    const [initialLineSchedules, setInitialLineSchedules] = React.useState<LineScheduleMap>({});

    // Bulk edit state
    const [selectedLineIds, setSelectedLineIds] = React.useState<Set<string>>(new Set());
    const [bulkDate, setBulkDate] = React.useState<Date | undefined>(undefined);

    const [isSaving, setIsSaving] = React.useState(false);
    const [showErrorModal, setShowErrorModal] = React.useState(false);
    const [modalErrorMessage, setModalErrorMessage] = React.useState("");

    // Check for duplicate dates within a line item (only for added items with dates)
    const getLineWarningMessage = (lineId: string): string | undefined => {
        const schedule = lineSchedules[lineId];
        if (!schedule || schedule.items.length <= 1) {
            return undefined;
        }

        // Get dates from added items (skip first which is ASAP) that have dates selected
        const addedShipments = schedule.items.slice(1);
        const datesWithValues = addedShipments
            .filter(s => s.requestedDeliveryDate !== null)
            .map(s => s.requestedDeliveryDate);
        const uniqueDates = new Set(datesWithValues);

        if (datesWithValues.length !== uniqueDates.size) {
            return translate(
                "Warning: This line item has duplicate shipment dates. Please use unique dates for each shipment.",
            );
        }

        return undefined;
    };

    const hasDuplicateDates = React.useMemo(() => {
        return scheduleGroups.availableToSchedule.some(line => !!getLineWarningMessage(line.cartLine.id));
    }, [scheduleGroups.availableToSchedule, lineSchedules]);

    const getMinScheduleDate = (): Date => {
        const today = new Date();
        today.setDate(today.getDate() + Number(scheduledOrderMinimumTimeFrame || 0));
        return today;
    };

    const minScheduleDate = React.useMemo(() => getMinScheduleDate(), [scheduledOrderMinimumTimeFrame]);

    // if fulfillmentMethod is pickup or unauthenticated, all lines will show inside the unavailable for schedule
    React.useEffect(() => {
        if (!cartlinesGroups?.length) return;

        const canScheduleLine = (line: CartGroup) => {
            const qtyOnHand = line.cartLine.qtyOnHand;
            const isBackOrderProduct =
                qtyOnHand === 0 || (line.cartLine.qtyOrdered != null && line.cartLine.qtyOrdered > qtyOnHand);

            return (
                !line.cartLine.quoteRequired &&
                !parseBoolean(line.cartLine.properties.dropShipBlock) &&
                (!isBackOrderProduct || allowBackOrderedProductsInScheduledOrders)
            );
        };

        const groups: ScheduleOrderGroups =
            isPickup || !isAuthenticated
                ? { availableToSchedule: [], schedulingUnavailable: cartlinesGroups }
                : {
                      availableToSchedule: cartlinesGroups.filter(canScheduleLine),
                      schedulingUnavailable: cartlinesGroups.filter(line => !canScheduleLine(line)),
                  };

        setScheduleGroups(groups);
        const initialLinesSchedules = initializeLineSchedules(groups.availableToSchedule, groups.schedulingUnavailable);
        setLineSchedules(initialLinesSchedules);
        setInitialLineSchedules(initialLinesSchedules);
    }, [cartlinesGroups, isPickup]);

    const allSelectableIds = scheduleGroups.availableToSchedule.map(l => l.cartLine.id);
    const isAllSelected = allSelectableIds.length > 0 && allSelectableIds.every(id => selectedLineIds.has(id));
    const hasAnySelected = selectedLineIds.size > 0;
    const isBulkDateValid = bulkDate && bulkDate >= minScheduleDate;
    const canAddToSelected = hasAnySelected && isBulkDateValid;
    const [hasScheduleChanges, setHasScheduleChanges] = React.useState(false);

    const normalizeSchedule = (schedule: LineScheduleState) => {
        return {
            ...schedule,
            items: schedule.items.map(item => {
                return { requestedDeliveryDate: item.requestedDeliveryDate, qty: item.qty };
            }),
        };
    };

    // Check if user has made any scheduling changes (for Save button)
    const checkForChanges = () => {
        const hasScheduleChanges = Object.keys(lineSchedules).some(lineId => {
            const currentSchedule = lineSchedules[lineId];
            const initialSchedule = initialLineSchedules[lineId];

            if (!currentSchedule || !initialSchedule) {
                return false;
            }

            const normalizedCurrent = normalizeSchedule(currentSchedule);
            const normalizedInitial = normalizeSchedule(initialSchedule);

            return !isEqual(normalizedCurrent, normalizedInitial);
        });

        setHasScheduleChanges(hasScheduleChanges);
    };

    React.useEffect(() => {
        checkForChanges();
    }, [lineSchedules, initialLineSchedules]);

    const hasInvalidDate = React.useMemo(() => {
        return scheduleGroups.availableToSchedule.some(line => {
            const schedule = lineSchedules[line.cartLine.id];
            return schedule?.items.some(
                item => !item.requestedDeliveryDate && item.requestedDeliveryDateDisplay !== SHIPS_ASAP,
            );
        });
    }, [lineSchedules, scheduleGroups.availableToSchedule]);

    const allLinesHasOnlyShipASAP = React.useMemo(() => {
        return Object.values(lineSchedules).every(line => {
            const hasOneItem = line.items.length === 1;
            const hasNoRequestedDeliveryDate = !line.items[0]?.requestedDeliveryDate;

            return hasOneItem && hasNoRequestedDeliveryDate;
        });
    }, [lineSchedules]);

    const handleSelectAllChange = (_: React.SyntheticEvent, isChecked: boolean) => {
        if (isChecked) {
            setSelectedLineIds(new Set(allSelectableIds));
        } else {
            setSelectedLineIds(new Set());
            setBulkDate(undefined);
        }
    };

    const handleLineCheckboxChange = (lineId: string, isChecked: boolean) => {
        setSelectedLineIds(prev => {
            const next = new Set(prev);
            if (isChecked) {
                next.add(lineId);
            } else {
                next.delete(lineId);
            }
            return next;
        });
    };

    const handleBulkDateChange = ({ selectedDay }: { selectedDay?: Date }) => {
        setBulkDate(selectedDay);
    };

    const handleAddToSelected = () => {
        if (!bulkDate || selectedLineIds.size === 0) {
            return;
        }

        const dateString = convertDateToDateOnlyString(bulkDate);

        setLineSchedules(prev => {
            const updated = { ...prev };

            selectedLineIds.forEach(lineId => {
                if (updated[lineId]) {
                    // Add new shipment row with 0 qty
                    updated[lineId] = {
                        ...updated[lineId],
                        items: [
                            ...updated[lineId].items,
                            {
                                id: generateShipmentId(),
                                requestedDeliveryDate: dateString,
                                qty: 0,
                            },
                        ],
                    };
                }
            });

            return updated;
        });

        // Clear selections
        setSelectedLineIds(new Set());
        setBulkDate(undefined);
    };

    const removeEmptyShipments = (schedules: LineScheduleState[]): LineScheduleState[] => {
        return schedules.map(schedule => ({
            ...schedule,
            items: schedule.items.filter(item => !(item.qty === 0)),
        }));
    };

    const handleSaveSchedule = () => {
        setIsSaving(true);

        // cleanup lines and ignore the shipments that has 0 qty or empty date
        const newObject = structuredClone(Object.values(lineSchedules));
        try {
            saveScheduledLines({
                model: { schedules: removeEmptyShipments(newObject), isScheduledorderPage: true },
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("EIS_ScheduledLines_Saved"), messageType: "success" });
                    window.location.href = checkoutShippingPageUrl;
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.();
                    }
                },
            });
        } catch (error) {
            setModalErrorMessage(
                translate(
                    "Something went wrong updating your schedule. Please review your schedule on the Checkout Shipping page before placing your order.",
                ),
            );
            setShowErrorModal(true);
        } finally {
            setIsSaving(false);
        }
    };

    const redirectToShippingPage = () => {
        window.location.href = checkoutShippingPageUrl;
    };

    const handleErrorModalClose = () => {
        setShowErrorModal(false);
        redirectToShippingPage();
    };

    const handleAddShipmentDate = (lineId: string) => {
        setLineSchedules(prev => {
            if (!prev[lineId]) {
                return prev;
            }

            return {
                ...prev,
                [lineId]: {
                    ...prev[lineId],
                    items: [
                        ...prev[lineId].items,
                        {
                            id: generateShipmentId(),
                            requestedDeliveryDate: null, // User will select date
                            qty: 0,
                        },
                    ],
                },
            };
        });
    };

    const handleRemoveShipmentDate = (lineId: string, shipmentId: string) => {
        setLineSchedules(prev => {
            if (!prev[lineId]) {
                return prev;
            }

            const lineSchedule = prev[lineId];
            const shipmentToRemove = lineSchedule.items.find(s => s.id === shipmentId);
            const removedQty = shipmentToRemove ? shipmentToRemove.qty : 0;

            return {
                ...prev,
                [lineId]: {
                    ...lineSchedule,
                    items: lineSchedule.items
                        .filter(s => s.id !== shipmentId)
                        .map((shipment, index) => {
                            if (index === 0) {
                                // The first shipment (ASAP) should have the qty updated
                                return {
                                    ...shipment,
                                    qty: shipment.qty + removedQty,
                                };
                            }
                            return shipment;
                        }),
                },
            };
        });
    };

    const handleShipmentQtyChange = (lineId: string, shipmentId: string, qty: number) => {
        setLineSchedules(prev => {
            if (!prev[lineId]) {
                return prev;
            }

            // here we need also to update the ship ASAP qty
            const addedShipments = prev[lineId].items.slice(1);

            // Calculate qty assigned to added items
            let scheduledQty = 0;
            addedShipments.forEach(s => {
                if (s.id === shipmentId) {
                    scheduledQty += qty;
                } else {
                    scheduledQty += s.qty;
                }
            });

            // ASAP qty = cart qty minus all scheduled qty
            const asapQty = (prev[lineId].totalQty || 0) - scheduledQty;

            // Remaining qty available for new items (same as ASAP qty)
            const remainingQty = asapQty;
            return {
                ...prev,
                [lineId]: {
                    ...prev[lineId],
                    items: prev[lineId].items.map((s, index) => {
                        if (index === 0) {
                            return { ...s, qty: remainingQty };
                        }
                        if (s.id === shipmentId) {
                            return { ...s, qty };
                        }
                        return s;
                    }),
                },
            };
        });
    };

    const handleShipmentDateChange = (lineId: string, shipmentId: string, requestedDeliveryDate: string | null) => {
        setLineSchedules(prev => {
            if (!prev[lineId]) {
                return prev;
            }

            return {
                ...prev,
                [lineId]: {
                    ...prev[lineId],
                    items: prev[lineId].items.map(s => (s.id === shipmentId ? { ...s, requestedDeliveryDate } : s)),
                },
            };
        });
    };

    const renderBulkEditSection = () => (
        <StyledWrapper {...styles.bulkEditSection}>
            <Typography {...styles.subHeading}>
                {siteMessage("Schedule_Order_Bulk_Edit_Header") || translate("Bulk Date Assignment")}
            </Typography>
            <StyledWrapper {...styles.bulkEditSectionActionsWrapper}>
                <StyledWrapper {...styles.bulkEditWrapper}>
                    <StyledWrapper {...styles.bulkEditCheckboxWrapper}>
                        <Checkbox
                            {...styles.selectAllCheckbox}
                            checked={isAllSelected}
                            onChange={handleSelectAllChange}
                            data-test-selector="scheduleOrder_selectAll"
                        >
                            {translate("Select All")}
                        </Checkbox>
                    </StyledWrapper>
                    <StyledWrapper {...styles.bulkEditDatePickerWrapper}>
                        <DatePicker
                            {...styles.bulkDatePicker}
                            key={bulkDate ? bulkDate.toString() : "empty"}
                            label=""
                            selectedDay={bulkDate}
                            onDayChange={handleBulkDateChange}
                            disabled={!hasAnySelected}
                            dateTimePickerProps={{
                                minDate: minScheduleDate,
                            }}
                            sizeVariant="small"
                        />
                    </StyledWrapper>
                    <StyledWrapper>
                        <Button
                            {...styles.addToSelectedButton}
                            disabled={!canAddToSelected}
                            onClick={handleAddToSelected}
                            data-test-selector="scheduleOrder_addToSelected"
                        >
                            {translate("Add to Selected")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
                {renderActionButtons(true)}
            </StyledWrapper>
        </StyledWrapper>
    );

    const renderAvailableToScheduleSection = () => {
        if (scheduleGroups.availableToSchedule.length === 0) {
            return null;
        }

        return (
            <StyledWrapper {...styles.sectionWrapper}>
                <ScheduleOrderGroupHeader isAvailableToSchedule={true} />
                <StyledWrapper {...styles.lineItemsWrapper}>
                    {scheduleGroups.availableToSchedule.map(line => (
                        <ScheduleOrderLineItem
                            key={line.cartLine.id}
                            cart={cart!}
                            cartLine={line.cartLine}
                            isSchedulable={true}
                            isSelected={selectedLineIds.has(line.cartLine.id)}
                            onCheckboxChange={handleLineCheckboxChange}
                            scheduleState={lineSchedules[line.cartLine.id]}
                            minScheduleDate={minScheduleDate}
                            onAddShipmentDate={handleAddShipmentDate}
                            onRemoveShipmentDate={handleRemoveShipmentDate}
                            onShipmentQtyChange={handleShipmentQtyChange}
                            onShipmentDateChange={handleShipmentDateChange}
                            warningMessage={getLineWarningMessage(line.cartLine.id)}
                        />
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        );
    };

    const renderSchedulingUnavailableSection = () => {
        if (scheduleGroups.schedulingUnavailable.length === 0) {
            return null;
        }

        return (
            <StyledWrapper className="unavailable" {...styles.sectionWrapper}>
                <ScheduleOrderGroupHeader isAvailableToSchedule={false} />
                <StyledWrapper {...styles.lineItemsWrapper}>
                    {scheduleGroups.schedulingUnavailable.map(line => (
                        <ScheduleOrderLineItem
                            key={line.cartLine.id}
                            cart={cart!}
                            cartLine={line.cartLine}
                            isSchedulable={false}
                            isSelected={false}
                            scheduleState={lineSchedules[line.cartLine.id]}
                            minScheduleDate={minScheduleDate}
                        />
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        );
    };

    const renderActionButtons = (headerActions: boolean) => (
        <StyledWrapper {...styles.actionButtonsWrapper} {...{ headerActions }}>
            <Button
                {...styles.cancelButton}
                className={headerActions ? "cancelButton" : ""}
                onClick={redirectToShippingPage}
                data-test-selector="scheduleOrder_cancel"
            >
                {translate("Cancel")}
            </Button>
            <Button
                {...styles.saveButton}
                disabled={
                    !hasScheduleChanges || isSaving || hasInvalidDate || hasDuplicateDates || allLinesHasOnlyShipASAP
                }
                onClick={handleSaveSchedule}
                data-test-selector="scheduleOrder_save"
                className={headerActions ? "saveButton" : ""}
            >
                {isSaving ? <LoadingSpinner size={20} /> : translate("Save Schedule")}
            </Button>
        </StyledWrapper>
    );

    if (!cartlinesGroups?.length && !isLoadingScheduledLines) {
        return null;
    }

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={isLoadingScheduledLines}>
            <StyledWrapper {...styles.wrapper}>
                {renderBulkEditSection()}

                {renderAvailableToScheduleSection()}

                {renderSchedulingUnavailableSection()}

                {renderActionButtons(false)}

                {errorMessage && <Notify messageType="danger" body={errorMessage} />}

                <Modal
                    {...styles.errorModal}
                    headline={translate("Error")}
                    isOpen={showErrorModal}
                    handleClose={handleErrorModalClose}
                >
                    <Typography>{modalErrorMessage}</Typography>
                    <Button
                        onClick={handleErrorModalClose}
                        css={css`
                            margin-top: 20px;
                        `}
                    >
                        {translate("OK")}
                    </Button>
                </Modal>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ScheduleOrderWidget),
    definition: {
        group: XNGAGE_ELEMENTS,
        displayName: "Schedule Order Widget",
        allowedContexts: ["ScheduleOrderPage"] as any,
        fieldDefinitions: [],
    },
};

export default widgetModule;
